import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Building2,
  AlertCircle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { SystemAdminDashboard } from '@/components/dashboard/system-admin-dashboard';

interface DashboardStats {
  totalOrganizations?: number;
  pendingReviewCount?: number;
  approvedCount?: number;
  activatedCount?: number;
  awaitingPaymentCount?: number;
}

interface RecentOrganization {
  id: string;
  name: string;
  status: string;
  created_at: string;
  logo_url: string | null;
}

export default function HomePage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({});
  const [recentOrganizations, setRecentOrganizations] = useState<RecentOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // System admin gets the new comprehensive dashboard
  if (!authLoading && user && user.role === 'SYSTEM_ADMIN') {
    return <SystemAdminDashboard />;
  }

  // Redirect IT_TEAM/IT_ADMIN and DATA_TEAM/DATA_ADMIN away from home
  if (!authLoading && user) {
    if (user.role === 'IT_TEAM' || user.role === 'IT_ADMIN') {
      navigate('/it-tasks', { replace: true });
      return null;
    }
    if (user.role === 'DATA_TEAM' || user.role === 'DATA_ADMIN') {
      navigate('/data-tasks', { replace: true });
      return null;
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const supabase = createClient();

        // Get stats based on role
        if (user.role === 'SYSTEM_ADMIN') {
          // Admin sees all organizations
          const { count: total } = await supabase
            .from('organizations')
            .select('*', { count: 'exact', head: true });

          // Count from both organizations table and onboarding_queue
          const { count: pendingOrgs } = await supabase
            .from('organizations')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'PENDING_REVIEW');

          const { count: pendingQueue } = await supabase
            .from('onboarding_queue')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'PENDING_REVIEW');

          const pending = (pendingOrgs || 0) + (pendingQueue || 0);

          const { count: approved } = await supabase
            .from('organizations')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'APPROVED');

          const { count: activated } = await supabase
            .from('organizations')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'ACTIVATED');

          // Count organizations being configured (IT and Data configuration in progress)
          const { count: beingConfigured } = await supabase
            .from('organizations')
            .select('*', { count: 'exact', head: true })
            .in('status', ['IT_IN_PROGRESS', 'DATA_CONFIGURATION']);

          setStats({
            totalOrganizations: (total || 0) + (pendingQueue || 0),
            pendingReviewCount: pending,
            approvedCount: approved || 0,
            activatedCount: activated || 0,
            awaitingPaymentCount: beingConfigured || 0,
          });

          // Get recent organizations from organizations table
          const { data: recentOrgs } = await supabase
            .from('organizations')
            .select('id, name, status, created_at, logo_url')
            .order('created_at', { ascending: false })
            .limit(5);

          setRecentOrganizations(recentOrgs || []);
        } else if (user.role === 'PLATFORM_TEAM' || user.role === 'PLATFORM_ADMIN') {
          // Platform team sees pending review organizations from onboarding_queue
          const { count: pendingReview } = await supabase
            .from('onboarding_queue')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'PENDING_REVIEW');

          const { count: approved } = await supabase
            .from('onboarding_queue')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'APPROVED');

          // Count organizations pending activation (FINAL_ACTIVATION tasks with PENDING status)
          const { count: pendingActivation } = await supabase
            .from('tasks')
            .select('*', { count: 'exact', head: true })
            .eq('type', 'FINAL_ACTIVATION')
            .eq('status', 'PENDING')
            .is('location_id', null);

          // Count activated organizations
          const { count: activated } = await supabase
            .from('organizations')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'ACTIVATED');

          setStats({
            pendingReviewCount: pendingReview || 0,
            approvedCount: approved || 0,
            activatedCount: pendingActivation || 0,
            awaitingPaymentCount: activated || 0,
          });

          // Get all organizations from onboarding_queue
          const { data: queueOrgs } = await supabase
            .from('onboarding_queue')
            .select('id, name, status, created_at, logo_url')
            .order('created_at', { ascending: false })
            .limit(10);

          // Get organizations with FINAL_ACTIVATION PENDING tasks (ready for activation)
          const { data: activationTasks } = await supabase
            .from('tasks')
            .select(
              'organization:organizations(id, name, status, created_at, logo_url)'
            )
            .eq('type', 'FINAL_ACTIVATION')
            .eq('status', 'PENDING')
            .is('location_id', null)
            .order('created_at', { ascending: false })
            .limit(10);

          const pendingActivationOrgs = activationTasks?.map((task: any) => ({
            ...task.organization,
            status: 'PENDING_ACTIVATION'
          })) || [];

          // Combine and sort by created_at, removing duplicates by ID
          const orgMap = new Map();
          [...(queueOrgs || []), ...(pendingActivationOrgs || [])].forEach(org => {
            if (!orgMap.has(org.id)) {
              orgMap.set(org.id, org);
            }
          });

          const allOrgs = Array.from(orgMap.values())
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 10);

          setRecentOrganizations(allOrgs);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        console.error('Error fetching home page data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (authLoading || loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto'></div>
          <p className='mt-4 text-gray-600 dark:text-gray-400'>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <Card className='max-w-md'>
          <CardContent className='pt-6'>
            <p className='text-red-600 dark:text-red-400'>
              Error loading dashboard: {error.message}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const getRoleTitle = (role: string) => {
    const titles: Record<string, string> = {
      SYSTEM_ADMIN: 'System Administrator',
      PLATFORM_TEAM: 'Platform Team',
      IT_TEAM: 'IT Team',
      DATA_TEAM: 'Data Team',
    };
    return titles[role] || role;
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-gray-100'>
          Welcome back, {user.full_name || user.email}!
        </h1>
        <p className='text-gray-600 dark:text-gray-400 mt-1'>
          {getRoleTitle(user.role)}
        </p>
      </div>

      {/* Statistics Cards - Role-specific */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4`}>
        {(user.role === 'PLATFORM_TEAM' || user.role === 'PLATFORM_ADMIN') && (
          <>
            <Card>
              <CardHeader className='flex flex-row items-center justify-between pb-2'>
                <CardTitle className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Pending Review
                </CardTitle>
                <Clock className='h-4 w-4 text-yellow-600' />
              </CardHeader>
              <CardContent>
                <div className='text-2xl font-bold'>{stats.pendingReviewCount || 0}</div>
                <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
                  Awaiting your review
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='flex flex-row items-center justify-between pb-2'>
                <CardTitle className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Approved
                </CardTitle>
                <CheckCircle2 className='h-4 w-4 text-green-600' />
              </CardHeader>
              <CardContent>
                <div className='text-2xl font-bold'>{stats.approvedCount || 0}</div>
                <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
                  Approved by you
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='flex flex-row items-center justify-between pb-2'>
                <CardTitle className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Pending Activation
                </CardTitle>
                <AlertCircle className='h-4 w-4 text-orange-600' />
              </CardHeader>
              <CardContent>
                <div className='text-2xl font-bold'>{stats.activatedCount || 0}</div>
                <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
                  Ready for activation
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='flex flex-row items-center justify-between pb-2'>
                <CardTitle className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Activated
                </CardTitle>
                <CheckCircle2 className='h-4 w-4 text-green-600' />
              </CardHeader>
              <CardContent>
                <div className='text-2xl font-bold'>{stats.awaitingPaymentCount || 0}</div>
                <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
                  Live organizations
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Recent Organizations Grid */}
      {recentOrganizations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {user.role === 'SYSTEM_ADMIN'
                ? 'Recent Organizations'
                : 'Recent Organizations in Pipeline'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {recentOrganizations.map((org) => (
                <div
                  key={org.id}
                  className='p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-lg transition-shadow'
                >
                  <div className='flex items-start gap-3'>
                    {/* Organization Logo */}
                    {org.logo_url ? (
                      <div className='w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                        <img
                          src={org.logo_url}
                          alt={`${org.name} logo`}
                          className='w-full h-full object-cover'
                        />
                      </div>
                    ) : (
                      <div className='w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0'>
                        <Building2 className='w-6 h-6 text-gray-400' />
                      </div>
                    )}

                    {/* Organization Info */}
                    <div className='flex-1 min-w-0'>
                      <h3 className='font-semibold text-gray-900 dark:text-gray-100 truncate'>
                        {org.name}
                      </h3>
                      <span
                        className={`inline-block mt-2 px-2 py-1 text-xs font-medium rounded-full ${
                          org.status === 'ACTIVATED'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : org.status === 'PENDING_REVIEW'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : org.status === 'APPROVED'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : org.status === 'PENDING_ACTIVATION'
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {org.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {recentOrganizations.length === 0 && (
        <Card>
          <CardContent className='pt-6'>
            <div className='text-center py-8'>
              <Building2 className='h-12 w-12 mx-auto mb-4 text-gray-400' />
              <p className='text-gray-500 dark:text-gray-400'>
                No items requiring action at this time
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
